import { z } from 'zod';
import { ContactStatus } from '../../models';
/** Coerces the 'true'/'false' strings that arrive via querystrings */
const queryBooleanSchema = z.union([z.boolean(), z.enum([`true`, `false`])])
    .transform(value => value === true || value === `true`);
/** Accepts ?tags=a or ?tags=a&tags=b and always yields an array */
const queryStringArraySchema = z.union([
    z.string().trim().min(1),
    z.array(z.string().trim().min(1)),
]).transform(value => Array.isArray(value) ? value : [value]);
/** Accepts ?excludeGroupIds=1 or ?excludeGroupIds=1&excludeGroupIds=2 and always yields an array */
const queryNumberArraySchema = z.union([
    z.array(z.coerce.number().int().positive()),
    z.coerce.number().int().positive(),
]).transform(value => Array.isArray(value) ? value : [value]);
/** assignedUserId value matching invoices where neither the invoice nor its contact has an assignee */
export const INSIGHTS_UNASSIGNED = `unassigned`;
/**
 * Query params for the insights endpoint. The contact filters are a subset of
 * IContactParams - the contact_stats based filters (balances, avgDaysToPay etc)
 * are excluded as they are always "as of now" and don't compose with a
 * historical asOfDate.
 */
export const insightsQuerySchema = z.object({
    /** Point in time to calculate balances for. Defaults to today in the organisation timezone */
    asOfDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, `asOfDate must be yyyy-MM-dd`).optional(),
    /**
     * Also compute monthly figures for the last INSIGHTS_HISTORY_MONTHS months
     * (ending at asOfDate) and return them as `history`. Expensive - one full
     * point-in-time computation per month.
     */
    history: queryBooleanSchema.optional(),
    // Contact filters (evaluated against contacts as they are now)
    /** For searching the name, or account ID column */
    searchStr: z.string().trim().min(1).optional(),
    name: z.string().trim().min(1).optional(),
    groupId: z.coerce.number().int().positive().optional(),
    status: z.enum(ContactStatus).optional(),
    frozen: queryBooleanSchema.optional(),
    isJob: queryBooleanSchema.optional(),
    isProject: queryBooleanSchema.optional(),
    personName: z.string().trim().min(1).optional(),
    email: z.string().trim().min(1).optional(),
    emailOperator: z.enum([`contains`, `isEmpty`]).optional(),
    mobile: z.string().trim().min(1).optional(),
    mobileOperator: z.enum([`contains`, `isEmpty`, `isInvalid`]).optional(),
    externalAccountId: z.string().trim().min(1).optional(),
    externalAggregateName: z.string().trim().min(1).optional(),
    tags: queryStringArraySchema.optional(),
    /**
     * Effective assignee - the invoice's assignee wins, falling back to the
     * contact's when the invoice has none. Credit notes have no assignee of
     * their own so always use the contact's.
     */
    assignedUserId: z.union([z.literal(INSIGHTS_UNASSIGNED), z.guid()]).optional(),
    // Exclusions. Applied after the inclusive filters above - a contact matching
    // any excluded tag, or sitting in an excluded group, is dropped entirely.
    /** Contacts carrying any of these tags are excluded */
    excludeTags: queryStringArraySchema.optional(),
    /** Contacts in any of these groups are excluded. Ungrouped contacts are never excluded */
    excludeGroupIds: queryNumberArraySchema.optional(),
});
/** How many monthly points `history=true` returns (including the asOfDate point) */
export const INSIGHTS_HISTORY_MONTHS = 12;
//# sourceMappingURL=insights.js.map